<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  open: {
    type: Boolean,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selectedValues: {
    type: [String, Number, Array],
    default: () => [],
  },
});

const emit = defineEmits(['select', 'search']);

const { t } = useI18n();

const searchValue = defineModel('searchValue', {
  type: String,
  default: '',
});

const searchInput = ref(null);
const highlightedIndex = ref(-1);

// Reset (rather than preserve) the highlighted option whenever the dropdown
// opens or the visible option set changes (e.g. while typing a search term),
// so a stale index never points at an option that's no longer visible.
watch([() => props.open, () => props.options], () => {
  highlightedIndex.value = -1;
});

const isSelected = option => {
  if (Array.isArray(props.selectedValues)) {
    return props.selectedValues.includes(option.value);
  }
  return option.value === props.selectedValues;
};

const optionId = index => `${props.id}-option-${index}`;

const onInputSearch = event => {
  searchValue.value = event.target.value;
  emit('search', event.target.value);
};

const moveHighlight = delta => {
  const count = props.options.length;
  if (!count) return;
  const current = highlightedIndex.value;
  highlightedIndex.value =
    current === -1 && delta < 0 ? count - 1 : (current + delta + count) % count;
};

const selectHighlighted = () => {
  const option = props.options[highlightedIndex.value];
  if (option) emit('select', option);
};

const onSearchKeydown = event => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      moveHighlight(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      moveHighlight(-1);
      break;
    case 'Enter':
      if (highlightedIndex.value !== -1) {
        event.preventDefault();
        selectHighlighted();
      }
      break;
    default:
      break;
  }
};

defineExpose({
  focus: () => searchInput.value?.focus(),
});
</script>

<template>
  <div
    v-show="open"
    class="absolute z-50 w-full mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
  >
    <div class="relative border-b border-n-strong">
      <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
      <input
        ref="searchInput"
        :value="searchValue"
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded="true"
        :aria-controls="id"
        :aria-activedescendant="
          highlightedIndex !== -1 ? optionId(highlightedIndex) : undefined
        "
        :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
        @input="onInputSearch"
        @keydown="onSearchKeydown"
      />
    </div>
    <ul
      :id="id"
      class="py-1 mb-0 overflow-auto max-h-60"
      role="listbox"
      :aria-multiselectable="multiple"
    >
      <li
        v-for="(option, index) in options"
        :id="optionId(index)"
        :key="`${option.value}-${index}`"
        class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
        :class="{
          'bg-n-alpha-2': isSelected(option),
          'bg-n-alpha-1': !isSelected(option) && highlightedIndex === index,
        }"
        role="option"
        :aria-selected="isSelected(option)"
        @click="emit('select', option)"
        @mouseenter="highlightedIndex = index"
      >
        <span
          :class="{
            'font-medium': isSelected(option),
          }"
          class="text-n-slate-12"
        >
          {{ option.label }}
        </span>
        <span
          v-if="isSelected(option)"
          class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
        />
      </li>
      <li v-if="options.length === 0" class="px-3 py-2 text-sm text-n-slate-11">
        {{ emptyState || t('COMBOBOX.EMPTY_STATE') }}
      </li>
    </ul>
  </div>
</template>
